import { Injectable, EventEmitter } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';

import { of, Subject } from 'rxjs';
import { concatMap } from 'rxjs/operators';
import * as AWS from 'aws-sdk/global';
import * as S3 from 'aws-sdk/clients/s3';
import * as queue from 'queue';
import { environment } from 'environments/environment';
import { PromotionsService } from 'app/services/promotions.service';
import { CurrentUser } from 'app/services/currentUser.service';
import * as fromApp from '../../app.reducer';
import * as fromNew from './store/new.reducer';
import * as newActions from './store/new.actions';
import { FirebaseService } from 'app/services/firebase.service';
import { ProjectService } from 'app/services/project.service';
import { LoggingService, LogAppSection } from 'app/services/logging.service';
import { ImagePreprocessingService } from './image-preprocessing.service';
import { FacebookPixelService } from 'app/services/facebook-pixel.service';
declare const Buffer;

@Injectable()
export class NewStoryService {
  projectId: null | string;
  projectTitle: null | string;
  storyId;
  eventEmitter: EventEmitter<any> = new EventEmitter();
  onUploadReset: EventEmitter<any> = new EventEmitter();
  images = [];
  uploadStatus: fromNew.UploadStatus;
  isUploadCompletedMessageDisplaying = false;
  lastOrderDesignMode: string;
  storyCreated = false;
  designOptions = new Subject();
  s3keys = {};
  transferingImages;
  isDesignTipsShowing = false;
  uploadCancelled;
  updateProjectErrorCount = 0;
  thumbnailsUploadBegin = false;
  imagesUploadBegin = false;

  private baseUrl = environment.API;
  private albums: Array<Object> = [];
  private q = queue({ concurrency: 1 });

  constructor(
    private http: HttpClient,
    private router: Router,
    private firebaseService: FirebaseService,
    private promotionsService: PromotionsService,
    private currentUser: CurrentUser,
    private store: Store<fromApp.AppState>,
    private projectService: ProjectService,
    private loggingService: LoggingService,
    private imagePreprocessingService: ImagePreprocessingService,
    private facebookPixelService: FacebookPixelService,
  ) {
    this.store.select('new').subscribe(storyUploading => {
      this.uploadStatus = storyUploading.uploadStatus;
      this.projectId = storyUploading.uploadingProjectId;
      this.projectTitle = storyUploading.uploadingProjectTitle;
    });
  }

  selectedImages() {
    return this.images.filter(image => !image.unchecked);
  }

  getAlbums() {
    return this.albums;
  }

  unsetAlbums() {
    this.albums = [];
    this.eventEmitter.emit(this.albums);
  }

  hasAlbums() {
    return !!this.albums.length;
  }

  filterChosenImages() {
    this.images = this.images.filter(image => !image.unchecked);
  }

  startCreation(projectId, details) {
    if (!this.projectId ) {
      this.projectService.create(details);
    } else {
      this.uploadCancelled = false;
      this.store.dispatch(new newActions.UpdateSubmitting(true));
      this.filterChosenImages();
      return this.finishProjectCreation(projectId, details).subscribe(projectData => {
        this.store.dispatch(new newActions.SetProjectId(projectData['id']));
        this.loggingService.log('Project ID:', projectData['id'], {
          appSection: LogAppSection.StoryCreating,
        });
        this.store.dispatch(
          new newActions.SetProjectTitle(projectData['title'])
        );
        this.loggingService.log('Project Title:', projectData['title'], {
          appSection: LogAppSection.StoryCreating,
        });
        this.store.dispatch(new newActions.SetIsProjectUpdating(false));
        this.loggingService.log('Project Updating:', false, {
          appSection: LogAppSection.StoryCreating,
        });
        this.updateUploadStatus();
        this.waitForUpload();
        this.firebaseService.logEvent('design_request_project_create', {
          project_id: this.projectId,
        });
      });
    }
  }

  updateProject(details) {
    this.store.dispatch(new newActions.SetIsProjectUpdating(true));
    this.loggingService.log('Project Updating:', true, {
      appSection: LogAppSection.StoryCreating,
    });
    return this.updateProjectTitle(details, this.projectId)
      .pipe(
        concatMap(projectData => {
          if (projectData) {
            this.store.dispatch(
              new newActions.SetProjectTitle(projectData['title'])
            );
            this.loggingService.log('Project Title:', projectData['title'], {
              appSection: LogAppSection.StoryCreating,
            });
            this.firebaseService.logEvent('design_request_project_update', {
              project_id: this.projectId,
              title: projectData['title'],
            });
          }
          return this.updateCover(this.projectId);
        })
      )
      .subscribe(
        () => {
          this.updateProjectErrorCount = 0;
          this.store.dispatch(new newActions.SetIsProjectUpdating(false));
          this.loggingService.log('Project Updating:', false, {
            appSection: LogAppSection.StoryCreating,
          });
        },
        () => {
          this.updateProjectErrorCount++;
          if (this.updateProjectErrorCount < 3) {
            this.updateProject(details);
          } else {
            this.store.dispatch(new newActions.SetIsProjectUpdating(false));
            this.loggingService.log('Project Updating:', false, {
              appSection: LogAppSection.StoryCreating,
            });
          }
        }
      );
  }

  titleHaveNotChanged(title) {
    return this.projectTitle === title;
  }

  startUpload() {
    this.store.dispatch(new newActions.StartUpload());
    this.loggingService.log('Upload started', {
      appSection: LogAppSection.StoryCreating,
    });

    return this.addImagesToProject(this.projectId)
      .pipe(
        concatMap(data => {
          window.scrollTo(0, 0);
          this.transferingImages = data['results'];
          return this.fetchDesignOptionsForProject();
        }),
        concatMap(designOptions => {
          this.designOptions.next({ ...designOptions });
          return this.setupImageUpload(this.transferingImages);
        }),
        concatMap(data => {
          this.s3keys['thumbs'] = data;
          return this.setupImageUpload(this.transferingImages, true);
        })
      )
      .subscribe(data => {
        this.s3keys['hires'] = data;
        this.uploadProjectImages(this.s3keys);
      });
  }

  waitForUpload() {
    if (this.uploadsInfoReady()) {
      this.startUpload();
    } else {
      setTimeout(() => {
        this.waitForUpload();
      }, 0);
    }
  }

  uploadsInfoReady() {
    return this.images.every(image => {
      return true;
    });
  }

  finishProjectCreation(projectId, details) {
    this.isUploadCompletedMessageDisplaying = false;
    this.lastOrderDesignMode = details.design_mode;

    const projectPayload = {
      title: details.title,
    };

    this.store.dispatch(new newActions.SetIsProjectUpdating(true));
    this.loggingService.log('Project Updating: ' + true, {
      appSection: LogAppSection.StoryCreating,
    });
    return this.projectService.update(projectId, projectPayload);
  }

  updateProjectTitle(details, projectId) {
    this.isUploadCompletedMessageDisplaying = false;
    this.lastOrderDesignMode = details.design_mode;

    const projectPayload = {
      title: details.title,
    };

    if (this.titleHaveNotChanged(details.title)) {
      return of(false);
    }
    return this.projectService.update(projectId, projectPayload);
  }

  fetchDesignOptionsForProject() {
    const url = `${this.baseUrl}/designoptions/project/${this.projectId}/`;
    return this.http.get(url);
  }

  createStory(details, specs) {
    const url = `${this.baseUrl}/stories/project/${this.projectId}/`;

    this.images = this.selectedImages();
    this.updateUploadStatus();

    const storyPayload = {
      design_mode: details.design_mode,
      ...specs,
    };

    this.http.post(url, storyPayload).subscribe(storyData => {
      this.storyId = storyData['id'];
      this.router.navigate(['/stories/designing']);
      this.storyCreated = true;
      this.firebaseService.logEvent('design_request_story_create', {
          project_id: this.projectId,
          story_id: this.storyId,
          occasion : storyData['occasion'],
          style : storyData['style'],
          emb_level : storyData['embellishment_level'],
          sticker_lever : storyData['text_sticker_level'],
          // size : storyData['occasion'],
          density : storyData['image_density'],
          // photos_per_spread : storyData['occasion'],
          selected_image_count :   this.selectedImages().length,
          total_image_count : this.images.length,
      });
      this.updateUploadStatus();
    });
  }

  private updateCover(projectId) {
    const url = `${this.baseUrl}/images/project/${projectId}/`;
    const oldCoverIndex = this.transferingImages.findIndex(
      image => image.favorite
    );
    const newCoverIndex = this.images.findIndex(image => image.frontcover);
    if (oldCoverIndex === newCoverIndex) {
      return of(false);
    }
    this.transferingImages[oldCoverIndex].favorite = false;
    this.transferingImages[newCoverIndex].favorite = true;
    return this.http.put(url, this.transferingImages);
  }

  private addImagesToProject(projectId) {
    const url = `${this.baseUrl}/images/project/${projectId}/`;
    const projectImages = this.images.map((image , index) => {
      const settings = {
        source: 's3',
        filename: image.name,
        handle: '',
        width: image.originalWidth,
        height: image.originalHeight,
        taken_at: image.date,
        orientation: image.orientation,
        favorite: image.frontcover,
        selected: !image.unchecked,
        sequence: index,
      };
      if (image.similarity) {
        settings['similarity'] = image.similarity['0'];
      }
      if (image.longitude && image.latitude) {
        settings['latitude'] = image.latitude;
        settings['longitude'] = image.longitude;
      }
      return settings;
    });
    return this.http.post(url, { images: projectImages });
  }

  private setupImageUpload(images, hires = false) {
    const url = `${this.baseUrl}/images/upload/${hires ? '?hires' : ''}`;

    const imagesIds = images.map(image => {
      return image.id;
    });
    return this.http.post(url, { images: imagesIds });
  }

  private uploadProjectImages(data) {

    this.configureS3(data.thumbs.upload);
    const s3 = new S3({
      apiVersion: '2006-03-01',
      params: { Bucket: data.thumbs.upload.bucket_name },
    });
    data.thumbs.results.forEach((item, index) => {
      this.q.push(cb => {
        this.uploadThumbnail(
          s3,
          item,
          {
            thumbnail: new Buffer(this.images[index].dataUrl.replace(/^data:image\/\w+;base64,/, ''), 'base64'),
          },
          index,
          0,
          cb
        );
      });
    });
    this.q.push(cb => {
      this.configureS3(data.hires.upload, cb);
    });
    data.hires.results.forEach((item, index) => {
      this.q.push(cb => {
        this.uploadImage(s3, item, this.images[index].originalFile, index, 0, cb);
      });
    });
    this.q.start();
  }

  private configureS3(params, cb = null) {
    AWS.config.update({
      region: params.region_name,
      credentials: new AWS.CognitoIdentityCredentials({
        IdentityPoolId: params.identity_pool_id,
        IdentityId: params.identity_id,
        Logins: {
          'cognito-identity.amazonaws.com': params.token,
        },
      }),
    });
    if (cb) {
      cb();
    }
  }

  private uploadThumbnail(s3, item, file, index, tryNum, cb) {
    if (this.uploadStatus.thmbStatus !== 'uploading') {
      return;
    }

    const firebaseEventName = tryNum
      ? 'thumbnails_upload_resume'
      : !this.thumbnailsUploadBegin
      ? 'thumbnails_upload_begin'
      : '';
    if (firebaseEventName) {
      this.thumbnailsUploadBegin = true;
      const params = tryNum
        ? {
            project_id: this.projectId,
            upload_target: this.images.length,
            upload_count: index + 1,
            image_id: item.id,
            filename: file.file.name,
            progress: this.uploadStatus.thumbnailPercent,
          }
        : {
            project_id: this.projectId,
            upload_target: this.images.length,
          };
      this.firebaseService.logEvent(firebaseEventName, params);
    }

    s3.upload(
      {
        Key: item.key,
        Body: file.thumbnail,
        ACL: 'private',
        ContentEncoding: 'base64',
        ContentType: 'image/jpeg',
      },
      (err, data) => {
        if (err) {
          const nextTryNum =
            err.name === 'RequestAbortedError' ? tryNum : tryNum + 1;
          this.uploadThumbnail(s3, item, file, index, nextTryNum, cb);
          this.firebaseService.logEvent('image_upload_transfer_error', {
            image_id: item.id,
            upload_count: index + 1,
            upload_target: this.images.length,
            project_id: this.projectId,
            reason: err,
          });
        } else {
          if (this.uploadStatus.thmbStatus === 'uploading') {
            this.images[index].thumbUploaded = true;
            this.updateUploadStatus();
            cb();
          }
        }
      }
    );
  }

  private uploadImage(s3, item, file, index, tryNum, cb) {
    if (this.uploadStatus.status !== 'uploading') {
      return;
    }

    const firebaseEventName = tryNum
      ? 'images_upload_resume'
      : !this.imagesUploadBegin
      ? 'images_upload_begin'
      : '';
    if (firebaseEventName) {
      this.imagesUploadBegin = true;
      const params = tryNum
        ? {
            project_id: this.projectId,
            upload_target: this.images.length,
            upload_count: index + 1,
            image_id: item.id,
            filename: file.name,
            progress: this.uploadStatus.progressPercent,
          }
        : {
            project_id: this.projectId,
            upload_target: this.images.length,
          };
      this.firebaseService.logEvent(firebaseEventName, params);
    }

    s3.upload(
      {
        Key: item.key,
        Body: file,
        ACL: 'private',
        ContentEncoding: '',
        ContentType: '',
      },
      (err, data) => {
        if (err) {
          const nextTryNum =
            err.name === 'RequestAbortedError' ? tryNum : tryNum + 1;
          this.uploadImage(s3, item, file, index, nextTryNum, cb);
          this.firebaseService.logEvent('image_upload_transfer_error', {
            image_id: item.id,
            upload_count: index + 1,
            upload_target: this.images.length,
            project_id: this.projectId,
            reason: err,
          });
        } else {
          if (this.uploadStatus.status === 'uploading') {
            this.images[index].uploaded = true;
            this.updateUploadStatus();
            cb();
          }
        }
      }
    );
  }

  cancelUploading(redirectUrl = '/stories') {
    this.uploadCancelled = true;

    this.projectService.cancel(this.projectId).subscribe(
      () => {
        this.initUploadStatus();

        this.firebaseService.logEvent('design_request_project_cancel', {
          project_id: this.projectId,
        });

        if (this.images.find(image => !image.thumbUploaded)) {
          this.firebaseService.logEvent('thumbnails_upload_cancel', {
            project_id: this.projectId,
            upload_target: this.images.length,
            upload_count: this.images.filter(image => image.thumbUploaded)
              .length,
          });
        }

        if (this.images.find(image => !image.uploaded)) {
          this.firebaseService.logEvent('images_upload_cancel', {
            project_id: this.projectId,
            upload_target: this.images.length,
            upload_count: this.images.filter(image => image.uploaded).length,
          });
        }
      },
      err => {
        this.loggingService.error(err.message);
      }
    );

    this.router.navigate([redirectUrl]);
  }

  initUploadStatus() {
    this.store.dispatch(new newActions.InitUpload());
    this.loggingService.log('Upload inited', {
      appSection: LogAppSection.StoryCreating,
    });
    this.onUploadReset.emit();
    this.q.end();
    this.store.dispatch(new newActions.UpdateSubmitting(false));
    this.uploadCancelled = false;
  }

  private updateUploadStatus() {
    if (!this.images || !this.currentUser.get()) {
      this.initUploadStatus();
      return;
    }

    const thumbUploaded = this.images.filter(image => image.thumbUploaded);
    const thumbnailPercent = (thumbUploaded.length / this.images.length) * 100;
    const thumbnailCounter = `${thumbUploaded.length}/${this.images.length}`;
    const thmbStatus = thumbnailPercent < 100 ? 'uploading' : 'finished';
    const uploaded = this.images.filter(image => image.uploaded);
    const progressPercent = (uploaded.length / this.images.length) * 100;
    const progressCounter = `${uploaded.length}/${this.images.length}`;
    const status =
      progressPercent === 0
        ? 'waiting'
        : progressPercent < 100
        ? 'uploading'
        : 'finished';

    const uploadStatus = {
      thumbnailPercent,
      thumbnailCounter,
      thmbStatus,
      progressPercent,
      progressCounter,
      status,
    };
    this.store.dispatch(new newActions.UpdateStatus(uploadStatus));
    this.store.dispatch(new newActions.UpdateSubmitting(true));

    if (thmbStatus === 'finished' && status === 'waiting') {
      uploadStatus['status'] = 'uploading';
      this.store.dispatch(new newActions.UpdateStatus(uploadStatus));

      this.firebaseService.logEvent('thumbnails_upload_complete', {
        project_id: this.projectId,
        upload_count: this.images.length,
      });
    }

    if (status === 'finished') {
      this.firebaseService.logEvent('images_upload_complete', {
        project_id: this.projectId,
        upload_count: this.images.length,
      });
    }

    if (
      status === 'finished' &&
      this.storyCreated &&
      !this.isDesignTipsShowing
    ) {
      this.initUploadStatus();
      this.imagePreprocessingService.clear();
      this.promotionsService.clear();
      this.storyCreated = false;
    }
  }
}
